import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { DocumentApiProvider, useDocumentApi } from "../Services/DocumentApiProvider";
import { useAlert, AlertProvider } from "../Components/AlertContext";
import { useConfirmation, ConfirmationProvider } from "../Components/ConfirmationContext";
import SureLoader from "../Components/SureLoader";
import Button from "../Components/Buttons";
import { CustomInput } from '../Components/CustomFormElements';
import Breadcrumbs from "../Components/Breadcrumbs";

function CreateDocument({canEdit}) {
    const { organizationId } = useParams();
    const { uploadDocument, uploadDocumentFromLink } = useDocumentApi();
    const { showAlert } = useAlert();
    const { showConfirmation } = useConfirmation();
    const navigate = useNavigate();

    const [file, setFile] = useState(null);
    const [link, setLink] = useState("");
    const [isDragging, setIsDragging] = useState(false); 
    const [isValidLink, setIsValidLink] = useState(true);
    const [isUploading, setIsUploading] = useState(false);
    const fileInputRef = useRef(null);

    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

    useEffect(() => {
        const inputField = document.getElementById("document-link");
        if (inputField) {
            inputField.addEventListener("focus", () => {
                inputField.classList.remove("input-error");
            });
        }
    }, []);

    const handleFileInputChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) handleFile(selectedFile);
    };

    const handleFile = (selectedFile) => {
        if (fileInputRef.current) fileInputRef.current.value = null;

        const allowedTypes = [".docx", ".doc", ".ppt", ".pptx", ".xlsx", "xls", ".pdf", ".txt", ".md", ".csv"];
        const fileName = selectedFile.name.toLowerCase();
        const isValidFile = allowedTypes.some(type => fileName.endsWith(type));

        if (!isValidFile) {
            showAlert("fail", "Invalid file type. Only .docx, .doc, .ppt, .pptx, .xlsx, xls, .pdf, .txt .md and .csv files are allowed.");
            return;
        }

        if (selectedFile.size > MAX_FILE_SIZE) {
            showAlert("fail", "File size exceeds the 10MB limit. Please select a smaller file.");
            return;
        }

        setFile(selectedFile);
        setLink(""); // Clear the link when a file is selected
    };

    const openFileDialog = () => {
        if (fileInputRef.current) fileInputRef.current.click();
    };
    const handleDragOver = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragging(true);
    };
    
    const handleDragLeave = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragging(false);
    };
    
    const handleDrop = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragging(false);
    
        if (link) {
            showAlert("warning", "Remove the link before uploading a file.");
            return;
        }
    
        const droppedFiles = Array.from(event.dataTransfer.files);
        if (droppedFiles.length > 1) {
            showAlert("warning", "Only one file can be uploaded at a time.");
            return;
        }
    
        const selectedFile = droppedFiles[0];
        handleFile(selectedFile); 
    };

    const validateForm = () => {
        if (!file && !link.trim()) {
            showAlert("fail", "Please provide either a file or a link.");
            return false;
        }

        if (link.trim() && !/^(https?:\/\/)([\w.-]+)\.([a-z]{2,6})(\/[^\s]*)?\/?$/.test(link)) {
            showAlert("fail", "Invalid link format. Please enter a valid URL.");
            setIsValidLink(false)
            return false;
        }

        return true;
    };

    const handleCreateDocument = async () => {
        if (!validateForm()) return;

        setIsUploading(true);

        try {
            if (file) {
                await uploadDocument(file);
            } else if (link.trim()) {
                await uploadDocumentFromLink(link);
            }

            navigate(`/organizations/${organizationId}/documents-hub`, {
                state: { successMessage: "Document uploaded successfully!" }
            });
        } catch (error) {
            console.error("Error during document upload", error);
            showAlert("fail", "An error occurred during the upload. Please try again.");
        } finally {
            setIsUploading(false);
        }
    };

    const handleCancel = () => {
        showConfirmation(
            "Are you sure you want to cancel creating a new document?",
            () => navigate(`/organizations/${organizationId}/documents-hub`)
        );
    };
    const handleLinkChange = (id, value) => {
        setLink(value);
        setFile(null);
        setIsValidLink(true);
    };

    const handleClear = () => {
        setFile(null);
        setLink("");
    };

    return (
        <div className="container">
            <Breadcrumbs/>
            {isUploading && <SureLoader fullscreen />}
            <div className="row d-flex gap">
                <div className="d-flex radius-yes p-2 bg-yes col-6 flex-column">
                    <div className="int-col-4">
                        <h2 className="h2-title">Create a New Document</h2>
                    </div>

                    <div className="int-col-4 gap d-flex flex-row-to-col flex-column">
                        <div className="int-col-2 d-flex p-2 border-yes radius-yes full-width-on-sml-scr gap flex-column">

                            {/* Upload Document Section */}
                            <div
                                className={`custom-file-upload-dropzone ${isDragging ? "dragging" : ""}`}
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                                onDrop={handleDrop}
                                onClick={openFileDialog} // file dialog
                            >
                                <input 
                                    ref={fileInputRef}
                                    type="file"
                                    className="file-input"
                                    onChange={handleFileInputChange}
                                    style={{ display: "none" }}
                                    disabled={!!link}
                                />
                                <label className="custom-file-upload-label">
                                    Drag & drop a file here or <span className="custom-file-upload-button">Browse Files</span>
                                </label>
                                <p className="custom-file-upload-info">
                                    Max file size: {MAX_FILE_SIZE / 1024 / 1024} MB | Allowed formats: DOCX, XLS, PDF, TXT
                                </p>
                                {file && <span>{file.name} ({(file.size / 1024).toFixed(2)} KB)</span>}
                            </div>
                            

                            {/* OR Link Section */}
                            <div className="section-separator w-100 d-flex justify-center p-2 large-font">Or provide a link to the site</div>
                            <div className='input-row d-flex flex-row justify-between'>
                                <div className='input-description'>
                                    <div className="input-title">Link to site</div>
                                    <div className="small-font">Enter URL</div>
                                </div>
                                <div className='align-wrapper d-flex align-center'>
                                <CustomInput
                                    id="document-link"
                                    label="Link"
                                    placeholder="Enter document link..."
                                    value={link}
                                    onChange={handleLinkChange}
                                    type="text"
                                    disabled={!!file}
                                    className={isValidLink ? "input-ok" : "input-error"} 
                                />
                                </div>
                            </div>
                        </div>
                        {/* Buttons */}
                        <div className="buttons-block w-100 justify-end d-flex flex-row flex-row-to-col">
                            {canEdit &&(
                                <Button variant="primary" size="medium" onClick={handleCreateDocument} disabled={isUploading}>
                                    {isUploading ? "Uploading..." : "Create Document"}
                                </Button>
                            )}
                            <Button variant="primary" size="medium" onClick={handleClear}>
                                Clear
                            </Button>
                            <Button variant="danger" size="medium" onClick={handleCancel}>
                                Cancel
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

// export default CreateDocument;
function NewDocumentWrapper({canEdit}) {
    return (
         <AlertProvider>
            <ConfirmationProvider>
                <DocumentApiProvider>
                    <CreateDocument canEdit={canEdit}/>
                </DocumentApiProvider>
            </ConfirmationProvider>
        </AlertProvider>
    );
}

export default NewDocumentWrapper;